/** The visual language, as data.
 *
 * Every colour, metric and material in the UI is named here once. `css.ts`
 * turns these into CSS custom properties; components never hard-code a hex.
 * This module is deliberately free of React and of the DOM so it can be
 * imported by a test, by a screen, or by the asset tooling.
 *
 * The palette is Heroes-III-shaped rather than modern-flat: the panels are
 * carved walnut, the recesses are shadowed stone, the edging is worn gilt and
 * the text plates are parchment. Nothing here is a pure grey.
 */

// Pure and window-free, through the same subpath the bitmap text uses: the
// type floor is derived from the bitmap cap floor so the two families are
// held to one measure.
import { deviceCapFloor } from "@game/render/text/glyphs";

// ------------------------------------------------------------------ palette

/** Base material colours. Suffixes read as: darkest -> lightest. */
export const PALETTE = {
  /** Carved walnut, the body of every panel. */
  wood0: "#1c1109",
  wood1: "#2c1c0f",
  wood2: "#3d2716",
  wood3: "#54371f",
  wood4: "#6b4728",
  wood5: "#86603c",

  /**
   * Cut stone, for recesses and the map surround - and it is a **warm** grey.
   *
   * This ramp used to be blue: `#0d0f12 #191d23 #272d36 #3a424e #525c6b`, R-B
   * between -5 and -25 at every step, which made `--hm-stone-2` the only cool
   * colour on a build of walnut and gilt. It is the ground of `.hs-screen`, the
   * face of every stone panel and the floor of the map surround, so it set the
   * temperature of most of the pixels on screen.
   *
   * The classic has no cool grey indoors. Counted as the fraction of pixels with
   * B-R>6: the reference hero sheet is 4.83%, the five reference adventure
   * sidebars 0.76-11.56%; ours were 17.64% (hero, whole frame), 56.22% (the
   * hero's skills column) and 22.08% (adventure sidebar).
   *
   * The luminance ramp is unchanged to within 1.0 at every step - 15.5, 29.4,
   * 45.4, 66.4, 92.5 against the old 14.6, 28.9, 44.6, 65.4, 91.4 - so nothing
   * that was legible against a step stops being legible. Only the hue moved.
   */
  stone0: "#130f0a",
  stone1: "#211d16",
  stone2: "#322d24",
  stone3: "#484236",
  stone4: "#645c4c",

  /** Worn gilt. `gold2` is the hairline; `gold3` is the highlight. */
  gold0: "#2f2109",
  gold1: "#6b4d15",
  gold2: "#a8801f",
  gold3: "#d9b25a",
  gold4: "#f4e0a3",

  /** Aged parchment, for text plates and tooltips. */
  parch0: "#6b5a3c",
  parch1: "#9c8760",
  parch2: "#c8b184",
  parch3: "#e6d5ac",

  /**
   * Ink on parchment, and the lit text on wood.
   *
   * `textFaint` used to be `#8a7a5d`, and against `wood2` - the sidebar's
   * hero and town rows, where it sets `Vanguard` and `Castle` under the name -
   * that is 3.35:1 (WCAG, Rec.709 luminance), under the 4.5:1 a small
   * caption needs; on `wood3` it was 2.58:1. `#b0a07c` was 5.44:1 on `wood2`
   * and 4.2:1 on `wood3`, one step under `textDim` (5.9 / 4.55) so the
   * hierarchy survives. Faint means quieter, not unreadable.
   *
   * Both moved again on 2026-09-04, and the reason is that the TOKEN is not
   * the ground. The sidebar's recesses are painted wood with grain and a
   * relief lip, and the plank a caption actually sits on measures lighter
   * than `wood2`: (85, 62, 34) under the roster's `Vanguard`, (90, 60, 37)
   * under the sheet's `Level 1 Knight`, (87, 58, 32) under the spellbook's
   * subtitle - relative luminance 0.05-0.06 against the token's 0.015. On
   * those grounds `#b9a683` measured 4.2-4.35:1 and `#b0a07c` 3.6-4.05:1,
   * both under the 4.5:1 a caption needs (WCAG, Rec.709 luminance, ink
   * colour against the median of the pixels around the glyphs on the shipped
   * 1920x1080 adventure and spellbook shots). `#d4c3a0` (L 0.556) is
   * 5.5-5.8:1 on the same grounds and `#c9b894` (L 0.488) 4.9-5.1:1; `text`
   * at 0.766 still stands clear above both, so the three-step hierarchy
   * survives, one step brighter across the board.
   */
  ink: "#241a0e",
  text: "#efe2c2",
  textDim: "#d4c3a0",
  textFaint: "#c9b894",

  /** Semantic accents. */
  good: "#7fbf5a",
  warn: "#d9a13a",
  bad: "#c04a34",
  mana: "#5b8fd6",
  move: "#c9a03a",
  fog: "#05060a",
} as const;

export type PaletteKey = keyof typeof PALETTE;

/** The eight the classic player colours, keyed by the `colour` string in `PlayerState`. */
export const PLAYER_COLOURS: Readonly<Record<string, string>> = Object.freeze({
  red: "#d0342c",
  blue: "#3b6fd4",
  tan: "#c39b5e",
  green: "#3f9b46",
  orange: "#e08a2a",
  purple: "#8b52c0",
  teal: "#2fa79b",
  pink: "#dd6fa8",
});

/** Neutral / unowned. */
export const NEUTRAL_COLOUR = "#8d8878";

/**
 * A player's swatch. `PlayerState.colour` is a free string in the kernel, so a
 * scenario may hand us a literal CSS colour; anything unrecognised falls back to
 * neutral rather than throwing, because a renderer must never fail on data.
 */
export function playerColour(colour: string | null | undefined): string {
  if (colour === null || colour === undefined || colour === "") return NEUTRAL_COLOUR;
  if (colour.charCodeAt(0) === 35) return colour; // "#rrggbb" straight through
  return PLAYER_COLOURS[colour] ?? NEUTRAL_COLOUR;
}

// ------------------------------------------------------------------ metrics

/**
 * Layout metrics in *design pixels*. The rendered size is this times
 * `--ui-scale`, so a number here is a ratio, not a promise about a display.
 */
export const METRICS = {
  /** Width of the right-hand command column. */
  sidebarWidth: 236,
  /** Height of the resource bar along the bottom. */
  resourceBarHeight: 40,
  /** Height of the one-line hint bar. */
  statusBarHeight: 22,
  /** The minimap is square and fills the sidebar minus its frame. */
  minimapInset: 8,
  panelPad: 6,
  gutter: 4,
  /** Portrait thumbnails: 256px source, so only exact divisors are used. */
  portraitSmall: 32,
  portraitMedium: 64,
  /** Icon sprites: 96px source, so only exact divisors are used. */
  iconSmall: 16,
  iconMedium: 24,
  iconLarge: 32,
} as const;

// -------------------------------------------------------------------- fonts

/**
 * The bundled serif leads (`BUNDLED_FONT_FAMILY`, declared in `css.ts`); the
 * rest of the stack is the fallback for a file that did not arrive, and the
 * note below is the history of why it was once the whole answer.
 *
 * No webfont was bundled: a missing font file at 1366x768 is a worse failure
 * than a system serif. The stack led with the faces that actually ship on
 * the three desktop platforms and carry a convincing engraved look.
 *
 * ## Why these serifs, and why the stack is not the fix
 *
 * Every reading in this build is a bitmap glyph out of `bitmapText.tsx`, and
 * most of them sit on the same plate as a line of this stack. Three reviews
 * named the pair as the tell that these screens are not the commercial game.
 * Swapping Palatino - a calligraphic humanist face with high stroke contrast, a
 * pen angle in every terminal and wide round bowls - for a low-contrast slab
 * serif narrows the gap and does not close it, because the gap was never the
 * DRAWING. It was the MEDIUM: an anti-aliased run and a hard-pixel run on one
 * row. That is settled in `css.ts` by `.hm-alias`, which cuts the coverage ramp
 * off every typeset leaf; see the block above that rule.
 *
 * The stack still matters, and this is why it is these two. Charter's brief was
 * rendering on a COARSE device grid - low stroke contrast, flat slabby serifs,
 * terminals cut square - and Century Schoolbook is the sturdy, large-x-height,
 * low-contrast schoolbook serif behind it. A face with hairline strokes loses
 * them to the alpha cut; a face with square terminals comes through it looking
 * like a stamped face rather than a damaged one.
 *
 * `C059` is named explicitly after Century Schoolbook, and it is not
 * decoration: the metric-alias file that maps `Century Schoolbook` onto it is
 * not installed everywhere, and the Charter that ships with X here is a Type 1
 * `.pfb`, which Chrome cannot load at all. Naming the OpenType family is what
 * makes what the screenshot harness renders the same thing a reader gets.
 *
 * The cost is measured, not assumed: on `Your hero casts all Air spells at
 * basic effectiveness.` C059 sets 5.8% wider than P052 (the Palatino this
 * replaces) at the same size, so a clamped description ellipsises about one
 * phrase earlier. Charter itself is 1.6% NARROWER than P052, so the platforms
 * that can load it pay nothing.
 *
 * This is deliberately a change of STACK and nothing else. Which elements are
 * typeset and which are stamped is each screen's decision, and moving that
 * boundary from here would relayout the whole build at once.
 */
export const FONTS = {
  display: '"Heroes Serif", "Trajan Pro", "Cinzel", Charter, "Bitstream Charter", "Century Schoolbook", C059, Georgia, serif',
  body: '"Heroes Serif", Charter, "Bitstream Charter", "Century Schoolbook", C059, Georgia, "Liberation Serif", serif',
  numeric: '"Iosevka", "DejaVu Sans Mono", "SF Mono", Menlo, Consolas, monospace',
} as const;

/**
 * The bundled serif. `css.ts` declares it with `@font-face` from
 * `apps/web/public/fonts`, four faces of Noto Serif (OFL) subset to Latin-1
 * and the punctuation the screens use, 25-29 KB each as woff2.
 *
 * Bundled after all, and the note above records why it was not: the stack it
 * leads is still there under it, so a missing file falls through to the same
 * system serif as before rather than to nothing. Two things the stack alone
 * could not give. The harness and a player's machine rendered DIFFERENT
 * faces - measured with `fc-match` on the screenshot box on 2026-09-03,
 * `Trajan Pro`, `Cinzel` and `Charter` all resolve to Noto Sans (not
 * installed; Chrome then walks on to C059), so every typeset run the critics
 * judged was C059 here and whatever a player's OS had elsewhere. And the size
 * floor is set in cap heights (`minDeviceTypePx`), which only means something
 * for a face whose cap-to-em ratio is known: Noto Serif's is 0.72 (measured
 * off the file at 100px: `H` 72 rows), the same as C059's, with an x-height
 * of 0.54 em against C059's 0.47 - the seven per cent that decides whether
 * an `e` still has a counter at 14 device pixels through the alias cut.
 */
export const BUNDLED_FONT_FAMILY = "Heroes Serif";

/** The four faces `css.ts` declares, by CSS weight and style, and the file each
 *  is served from under the app's public root. */
export const BUNDLED_FONT_FACES: readonly { readonly weight: number; readonly style: "normal" | "italic"; readonly file: string }[] = Object.freeze([
  { weight: 400, style: "normal", file: "/fonts/NotoSerif-Regular.woff2" },
  { weight: 700, style: "normal", file: "/fonts/NotoSerif-Bold.woff2" },
  { weight: 400, style: "italic", file: "/fonts/NotoSerif-Italic.woff2" },
  { weight: 700, style: "italic", file: "/fonts/NotoSerif-BoldItalic.woff2" },
]);

// ------------------------------------------------------------------ scaling

/**
 * The UI scales in coarse steps rather than continuously. A continuous scale
 * puts panel edges and 1px gilt hairlines on fractional device pixels, which is
 * exactly the blur this project is trying to avoid; a step keeps every border on
 * a whole pixel at the common resolutions.
 *
 * `minWidth`/`minHeight` are both required so an ultrawide 1080p monitor does
 * not get 4K-sized text.
 */
export interface ScaleStep {
  readonly minWidth: number;
  readonly minHeight: number;
  readonly scale: number;
}

export const SCALE_STEPS: readonly ScaleStep[] = Object.freeze([
  { minWidth: 0, minHeight: 0, scale: 1 },
  { minWidth: 1600, minHeight: 900, scale: 1.25 },
  { minWidth: 1920, minHeight: 1080, scale: 1.5 },
  { minWidth: 2560, minHeight: 1440, scale: 2 },
  { minWidth: 3840, minHeight: 2160, scale: 3 },
]);

/** The step a viewport lands on. Pure, and the single source of truth for the
 *  `@media` blocks emitted by `css.ts` - the test asserts they agree. */
export function pickScale(width: number, height: number): number {
  let scale = 1;
  for (const step of SCALE_STEPS) {
    if (width >= step.minWidth && height >= step.minHeight) scale = step.scale;
  }
  return scale;
}

/** Design pixels -> device pixels at a given scale, snapped to whole pixels so
 *  a border never lands on a half texel. */
export const snap = (designPx: number, scale: number): number =>
  Math.round(designPx * scale);

/**
 * The four type sizes, in design pixels.
 *
 * Here rather than inline in `css.ts` because they are snapped per scale step
 * the same way every other metric is, and the emitter needs the numbers to do
 * that. They used to be raw `calc(9px * var(--hm-scale))`, which is the one
 * metric family that was never snapped: at scale 1.25 that is 11.25 CSS px, and
 * a browser zoom multiplies *after* the cascade, so at 90% it reaches the glass
 * at 10.125 device pixels. Everything below `--hm-fs-body` then renders on
 * fractional texels.
 *
 * `tiny` is 10 rather than 9 for the same reason: at scale 1 on a small screen,
 * 9 design px zoomed to 90% is 8.1 device px, which is below what this face
 * stays legible at.
 */
export const FONT_SIZES = Object.freeze({
  tiny: 10,
  small: 11,
  body: 13,
  title: 15,
});

/**
 * The smallest em size, in DEVICE pixels, a typeset size is let shrink to
 * under a browser zoomed out.
 *
 * Browser zoom is honoured (the root is no longer transformed back to 100%;
 * see `@game/render`'s `text/devicePixels.ts`), so at 80% on a scale-1 window
 * `tiny` would reach the glass at 8 device pixels and `small` at 8.8 - below
 * the 9 the note on `FONT_SIZES.tiny` already calls illegible for this face
 * through the `.hm-alias` cut. Ten is the smallest of the four sizes at scale
 * 1, which is to say the smallest em this build has ever asked a serif to be
 * read at; a zoom-out may take a size down to it and no further. The three
 * small sizes meet there at 80% and the hierarchy above them survives.
 */
export const MIN_DEVICE_TYPE_PX = 14;

/**
 * Cap height as a fraction of the em, for the serif the floor is set in:
 * 0.72 for the bundled Noto Serif and for the C059 it falls back to, both
 * measured off the font file (an `H` at 100px stands 72 rows). It is what
 * turns a floor stated in cap rows into a `font-size`.
 */
export const TYPE_CAP_RATIO = 0.72;

/**
 * The type floor for a window `deviceHeight` device pixels tall, as an em
 * size in device pixels: the em whose CAP stands one row under the bitmap
 * cap floor of the same window (`deviceCapFloor` in `@game/render`), never
 * under `MIN_DEVICE_TYPE_PX`. 14 on a 768-tall window (cap 10 against the
 * bitmap floor's 11), 19 on a 1080-tall one (cap 13.7 against 15), 26 at
 * 1440.
 *
 * It used to be 10 and 14 - em sizes chosen as "the smallest the serif had
 * ever been read at", which put a 7 device px cap on the laptop and a 10 px
 * one on the monitor beside bitmap captions of 11 and 15 and the HD
 * Edition's own 16. The floor is stated in cap rows now for the same reason
 * the bitmap one is: a reader measures a caption by its capitals, and the em
 * of a serif is a third of it below the baseline and above the ascender.
 * One row under rather than level because a typeset run through the alias
 * cut (`.hm-alias`) reads a size heavier than a stamped face of the same
 * cap, and level with it the two stopped reading as a hierarchy.
 *
 * Unlike the old floor this one bites at 100% too: on a 768-tall window at
 * scale 1 `tiny`, `small` and `body` (10, 11, 13) all meet at 14 and `title`
 * (15) stays above; at the 1080p step `tiny` and `small` (15, 17) rise to 19
 * under `body` (20) and `title` (23). See `fontVars` in `css.ts`.
 */
export function minDeviceTypePx(deviceHeight: number): number {
  if (!(deviceHeight > 0)) return MIN_DEVICE_TYPE_PX;
  const cap = deviceCapFloor(deviceHeight) - 1;
  return Math.max(MIN_DEVICE_TYPE_PX, Math.round(cap / TYPE_CAP_RATIO));
}

/**
 * A type size's CSS value under device ratio `dpr`, snapped so it reaches the
 * glass on a whole number of device pixels and never below
 * `MIN_DEVICE_TYPE_PX` of them.
 *
 * Fractional CSS pixels on purpose: at 80% `body` at scale 1 is 13 * 0.8 =
 * 10.4 device px, rounds to 10, and 10 / 0.8 = 12.5 CSS px is the only value
 * that lands it there. At a ratio of 1 this is `snap(px, scale)` exactly.
 */
export function deviceSnappedFontPx(designPx: number, scale: number, dpr: number, floor = MIN_DEVICE_TYPE_PX): number {
  const ratio = dpr > 0 ? dpr : 1;
  const device = Math.max(floor, Math.round(snap(designPx, scale) * ratio));
  // Three decimals: finer than any zoom step a browser offers, and enough to
  // keep 12.5 as 12.5 rather than a float's idea of it.
  return Math.round((device / ratio) * 1000) / 1000;
}
